import React, { useState, useRef, useEffect } from 'react';
import { Form, InputGroup, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';

const SearchableSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select option...",
  className = "",
  size = "sm",
  isMulti = false,
  hideBadges = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (selectedOption) => {
    if (isMulti) {
      // Multiselect mode
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(selectedOption.value);
      
      if (isSelected) {
        // Remove from selection
        onChange(currentValues.filter(v => v !== selectedOption.value));
      } else {
        // Add to selection
        onChange([...currentValues, selectedOption.value]);
      }
      setSearchTerm('');
    } else {
      // Single select mode
      onChange(selectedOption.value);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleRemoveBadge = (valueToRemove) => {
    const currentValues = Array.isArray(value) ? value : [];
    onChange(currentValues.filter(v => v !== valueToRemove));
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const getDisplayValue = () => {
    if (searchTerm) return searchTerm;
    if (isMulti) {
      return ''; // For multiselect, we show badges instead
    }
    const selectedOption = options.find(opt => opt.value === value);
    return selectedOption ? selectedOption.label : '';
  };

  const getSelectedOptions = () => {
    if (!isMulti || !Array.isArray(value)) return [];
    return options.filter(opt => value.includes(opt.value));
  };

  const isOptionSelected = (optionValue) => {
    if (!isMulti) return value === optionValue;
    return Array.isArray(value) && value.includes(optionValue);
  };

  return (
    <div className={`position-relative ${className}`} ref={dropdownRef}>
      {/* Show selected items as badges for multiselect */}
      {isMulti && !hideBadges && getSelectedOptions().length > 0 && (
        <div className="mb-2 d-flex flex-wrap gap-1">
          {getSelectedOptions().map(option => (
            <Badge 
              key={option.value} 
              bg="primary" 
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem' }}
            >
              {option.label}
              <FontAwesomeIcon 
                icon={solid("times")} 
                className="ms-1" 
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveBadge(option.value);
                }}
              />
            </Badge>
          ))}
        </div>
      )}
      
      <InputGroup size={size}>
        <Form.Control
          ref={inputRef}
          type="text"
          value={getDisplayValue()}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
        />
        <InputGroup.Text 
          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
          onClick={handleInputClick}
        >
          <FontAwesomeIcon 
            icon={isOpen ? solid("chevron-up") : solid("chevron-down")} 
            size="sm" 
          />
        </InputGroup.Text>
      </InputGroup>

      {isOpen && (
        <ListGroup 
          className="position-absolute w-100 shadow-sm border-0 rounded-bottom"
          style={{ 
            zIndex: 1050, 
            maxHeight: '200px', 
            overflowY: 'auto',
            top: '100%',
            fontSize: size === 'sm' ? '0.8rem' : '1rem',
            marginTop: '-1px'
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <ListGroup.Item
                key={option.value}
                action
                active={isOptionSelected(option.value)}
                onClick={() => handleSelect(option)}
                className="border-start-0 border-end-0 d-flex align-items-center justify-content-between"
                style={{ 
                  cursor: 'pointer',
                  fontSize: size === 'sm' ? '0.8rem' : '1rem'
                }}
              >
                <span>{option.label}</span>
                {isMulti && isOptionSelected(option.value) && (
                  <FontAwesomeIcon icon={solid("check")} className="text-primary" />
                )}
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item className="text-muted border-start-0 border-end-0" disabled>
              No options found
            </ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default SearchableSelect;
